/**
 * T.10 S18 (D126, D127). A summary never names the job the member is
 * applying to: "Looking for an IT Support Specialist role at Riverbend
 * Medical Group" reads as a template, and recruiters trust the whole resume
 * less for it (Jackson, 21 Oct). Pure; shared by the wizard and the subgraph.
 */

export interface TargetNames {
  /** The posting's title and employer, or the target role sent to Via. */
  names: readonly (string | null | undefined)[];
  /**
   * The member's own words: a name already in them is not counted (a U.S.
   * Army veteran applying to the Army). Review M2: text, or a list such as
   * memberOwnNames (their job titles and employers) with the text.
   */
  own?: string | readonly string[] | null;
}

/** Review M2: the member's own job titles and employers, one set for the server and the browser alike. */
export function memberOwnNames(jobs: readonly { title?: string | null; civilianTitle?: string | null; employer?: string | null }[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const j of jobs) {
    for (const v of [j.title, j.civilianTitle, j.employer]) {
      const name = squash(String(v ?? ''));
      if (name && !seen.has(name.toLowerCase())) {
        seen.add(name.toLowerCase());
        out.push(name);
      }
    }
  }
  return out;
}

/** A sentence that only states an aim, with or without a name in it. */
const AIM = /^(seeking|looking\s+(for|to)|targeting)\b/i;

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const squash = (s: string) => s.replace(/\s+/g, ' ').trim();
const has = (text: string, name: string) => new RegExp(`(^|[^A-Za-z0-9])${escape(name)}(?![A-Za-z0-9])`, 'i').test(text);

/**
 * The text's sentences. A period after a single capital letter ("U.S.", an
 * initial) does not end one.
 */
function sentences(text: string): string[] {
  return squash(text)
    .split(/(?<=[.!?])(?<!(?:^|[^A-Za-z])[A-Z]\.)\s+/)
    .filter(Boolean);
}

function checked(x: TargetNames): string[] {
  const own = typeof x.own === 'string' ? x.own : (x.own ?? []).join(' . ');
  return x.names
    .map((n) => squash(String(n ?? '')))
    .filter((n) => n.length >= 3 && !(own && has(own, n)));
}

/** The text without any sentence that names the target job or only states an aim; '' when none is left. */
export function dropTargetSentences(text: string | null | undefined, x: TargetNames): string {
  const names = checked(x);
  return sentences(String(text ?? ''))
    .filter((s) => !AIM.test(s) && !names.some((n) => has(s, n)))
    .join(' ');
}

/** Whether dropTargetSentences would drop anything. */
export function namesTarget(text: string | null | undefined, x: TargetNames): boolean {
  return dropTargetSentences(text, x) !== squash(String(text ?? ''));
}
